import {useRef} from "react";
import type {OttopilotCustomersWorkspaceCapabilityConfiguration as CustomersModuleConfiguration} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/ottopilotCustomersWorkspaceCapability/domain/OttopilotCustomersWorkspaceCapabilityConfiguration";
import type {OttopilotEventTemplate} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/ottopilotEventsWorkspaceCapability/domain/OttopilotEventTemplate";
import type {OrganizationalUnit} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/organizationalUnitsWorkspaceCapability/domain/OrganizationalUnit";
import type {OrganizationalUnitTemplate} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/organizationalUnitsWorkspaceCapability/domain/OrganizationalUnitTemplate";
import type {OttopilotEventContainer} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/ottopilotEventContainersWorkspaceCapability/domain/OttopilotEventContainer";
import {eventContainerStatusToDisplayName} from "~/utilities/domainStatusDisplayNames";
import type {User} from "@vesta/vos-types/modules/workspaceLevel/users/domain/User";
import type {ListNotesPanelOpenContext} from "~/components/notes/listNotesPanelTypes";
import {
    EMOTORAD__EVENT_CONTAINER__ASSIGNED_DEALER_NAME__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__CONTACT_NUMBER__COLUMN_ID,
    emotoradCustomerStatuses,
    formatEmotoradJourneyLifecycleStatusIdForDisplay,
    type EmotoradListModuleConfig,
} from "~/domainModels/workspaceSpecific/emotorad";
import type {Iso8601DateTime, Uuid} from "@vesta/common-type-definitions/typeDefinitions";
import type {EventWithEventContainer, EventContainerWithEvents} from "~/utilities/apis/api";
import {processOttopilotStringTemplate} from "@vesta/vos-types/modules/workspaceLevel/domain/OttopilotStringTemplate";
import type {ListCardStatus} from "~/components/ottopilotDataViewer/listStatusConfig";
import {
    createListStatusResolver,
    LIST_INQUEUE_ASSIGNMENT_STATUS,
    resolveListCardStatusFromColumnConfig,
    type ListStatusBoardConfig,
} from "~/components/ottopilotDataViewer/listStatusConfig";
import {
    createEmotoradJourneyListFunnelOptions,
    navigateToEmotoradScheduleCallRoute,
    ottopilotDialPhoneNumber,
    resolveListCardFooterWhenEnabled,
    type EmotoradScheduleNavigateFn,
} from "~/components/ottopilotDataViewer/listCardPresentation";
import {
    createOttopilotListView,
    useOttopilotListCardPresentation,
    type OttopilotListCardFooterBindings,
} from "~/components/ottopilotDataViewer/ottopilotListCardPresentation";
import type {RowEngagementHandlers} from "~/components/ottopilotDataViewer/rowEngagement/rowEngagementHandlers";
import {useOnlyEventContainerTemplate} from "~/contexts/workspaceEventContainerTemplatesContext";
import {useEventTemplates} from "~/contexts/workspaceEventTemplatesContext";
import {
    getOptionalEventContainerDispositionId,
    getOptionalEventDispositionId,
    requireEventTemplate,
} from "~/utilities/ottopilotSemanticColumns";

/* Handlers must match OttopilotListCardActionHandlers arity; unused positional params are intentional. */
/* eslint-disable @typescript-eslint/no-unused-vars */

function stringifyJourneyAdditionalValue(raw: unknown): string {
    if (raw === undefined || raw === null) {
        return "";
    }
    if (typeof raw === "string") {
        return raw;
    }
    if (typeof raw === "number" || typeof raw === "boolean") {
        return String(raw);
    }
    return "";
}

/** Calendar/list title templates often embed contact number; phone is shown on its own row on list cards. */
function omitJourneyContactFromMergedValues(merged: Record<Uuid, unknown>): Record<Uuid, unknown> {
    const next = {...merged};
    /** Empty string replaces placeholders; deleting the key leaves raw 👉…👆columnId👈 segments in the title. */
    next[EMOTORAD__EVENT_CONTAINER__CONTACT_NUMBER__COLUMN_ID] = "";
    return next;
}

export {createOttopilotListView};

export type EmotoradMySalesTeamListCardDeps = {
    workspaceId: Uuid;
    navigate: EmotoradScheduleNavigateFn;
    scheduleRoute: string;
    listModuleConfig: EmotoradListModuleConfig;
    listStatusBoard: ListStatusBoardConfig;
    selectedTemplateColumns: {name: Uuid; phoneNumber: Uuid} | undefined;
    assignedDealerColumnId: Uuid;
    organizationalUnits: ReadonlyArray<OrganizationalUnit>;
    organizationalUnitTemplates: ReadonlyArray<OrganizationalUnitTemplate>;
    getAdditionalColumnValueAsString: (user: User, columnId: Uuid) => string;
    formatUserStatusLabel: (status: string) => string;
    onOpenCreateDrawer: () => void;
};

export function useEmotoradMySalesTeamListCardPresentation(deps: EmotoradMySalesTeamListCardDeps) {
    const {
        workspaceId,
        navigate,
        scheduleRoute,
        listModuleConfig,
        selectedTemplateColumns,
        assignedDealerColumnId,
        organizationalUnits,
        organizationalUnitTemplates,
        getAdditionalColumnValueAsString,
        formatUserStatusLabel,
        onOpenCreateDrawer,
        listStatusBoard,
    } = deps;

    const resolveUserListStatus = createListStatusResolver<User>({
        columnConfig: listStatusBoard.columnConfig,
        getStatusValue: (user) => user.status,
        resolveDisplayName: ({row}) => formatUserStatusLabel(row.status),
    });

    return useOttopilotListCardPresentation<User>(
        {
            getDisplayName: (user) => {
                const sseName = selectedTemplateColumns !== undefined ? getAdditionalColumnValueAsString(user, selectedTemplateColumns.name) : "";
                return sseName.trim() === "" ? user.userId : sseName;
            },
            getPhoneNumber: (user) => {
                if (selectedTemplateColumns === undefined) {
                    return null;
                }
                const phoneNumber = getAdditionalColumnValueAsString(user, selectedTemplateColumns.phoneNumber);
                return phoneNumber === "" ? null : phoneNumber;
            },
            getStatus: (user) => resolveUserListStatus(user),
            getCreatedAt: (user) => user.createdAt,
            getAssignedOuName: (user) => {
                const assignedDealerRaw = user.additionalColumnValues[assignedDealerColumnId];
                const assignedDealerId =
                    typeof assignedDealerRaw === "string"
                        ? assignedDealerRaw
                        : Array.isArray(assignedDealerRaw) && typeof assignedDealerRaw[0] === "string"
                          ? assignedDealerRaw[0]
                          : null;
                const assignedDealerOu = assignedDealerId === null ? null : (organizationalUnits.find((ou) => ou.id === (assignedDealerId as Uuid)) ?? null);
                const assignedDealerTemplate =
                    assignedDealerOu === null ? null : (organizationalUnitTemplates.find((template) => template.id === assignedDealerOu.templateId) ?? null);
                if (assignedDealerOu === null || assignedDealerTemplate === null) {
                    return null;
                }
                return processOttopilotStringTemplate(assignedDealerTemplate.instanceDisplayNameTemplate, assignedDealerOu.additionalColumnValues);
            },
        },
        (user, bindings) => resolveListCardFooterWhenEnabled(listModuleConfig.showFooter, bindings, user.userId),
        {
            onPhoneClick: (user, _bindings) => {
                const phoneNumber = selectedTemplateColumns !== undefined ? getAdditionalColumnValueAsString(user, selectedTemplateColumns.phoneNumber) : "";
                ottopilotDialPhoneNumber(phoneNumber);
            },
            onScheduleClick: (_user, _footerId, _bindings) => {
                navigate({
                    to: scheduleRoute,
                    params: {workspaceId},
                    search: {},
                });
            },
            onDisposeClick: undefined,
            onCreateClick: (_user, _footerId, _bindings) => {
                onOpenCreateDrawer();
            },
            onNotesClick: undefined,
        },
    );
}

export type EmotoradListScheduleCallModalOpener = (eventContainer: OttopilotEventContainer) => void;

export type EmotoradEventListCardPresentationDeps = {
    eventTemplate: OttopilotEventTemplate;
    workspaceId: Uuid;
    navigate: EmotoradScheduleNavigateFn;
    listModuleConfig: EmotoradListModuleConfig;
    getOuDisplayName: (ouId: Uuid) => string | null;
    resolveEventListStatus: (event: EventWithEventContainer) => ListCardStatus;
    onDisposeRequest: (event: EventWithEventContainer) => void;
    onOpenCreateDrawer: () => void;
    openNotesPanel: (context: ListNotesPanelOpenContext) => void;
    notesEventContainerSourceLabel?: string;
    openScheduleCallModal?: EmotoradListScheduleCallModalOpener;
    rowEngagement?: RowEngagementHandlers<EventWithEventContainer>;
    sharedFooterBindings?: OttopilotListCardFooterBindings;
    isListCardFooterRestricted?: (event: EventWithEventContainer) => boolean;
};

export function useEmotoradEventListCardPresentation(deps: EmotoradEventListCardPresentationDeps) {
    const {
        eventTemplate,
        workspaceId,
        navigate,
        listModuleConfig,
        getOuDisplayName,
        resolveEventListStatus,
        onDisposeRequest,
        onOpenCreateDrawer,
        openNotesPanel,
        notesEventContainerSourceLabel = "OttopilotEventContainer",
    } = deps;

    return useOttopilotListCardPresentation<EventWithEventContainer>(
        {
            getDisplayName: (event) => {
                const titleTemplate = eventTemplate.configuration.eventPage.calendarConfig?.calendarTitleOttopilotStringTemplate;
                const merged: Record<Uuid, unknown> = omitJourneyContactFromMergedValues({
                    ...event.eventContainer.additionalColumnValues,
                    ...event.additionalColumnValues,
                });
                return titleTemplate !== undefined
                    ? processOttopilotStringTemplate(titleTemplate, merged)
                    : eventTemplate.configuration.displayNameVariants.singularSentenceCase;
            },
            getPhoneNumber: (event) => {
                const raw = event.eventContainer.additionalColumnValues[EMOTORAD__EVENT_CONTAINER__CONTACT_NUMBER__COLUMN_ID];
                const phone = stringifyJourneyAdditionalValue(raw).trim();
                return phone === "" ? null : phone;
            },
            getStatus: (event) => resolveEventListStatus(event),
            getCreatedAt: (event) => event.createdAt,
            getAssignedOuName: (event) => {
                const assignedDealerRaw = event.eventContainer.additionalColumnValues[EMOTORAD__EVENT_CONTAINER__ASSIGNED_DEALER_NAME__COLUMN_ID];
                const assignedDealerId = typeof assignedDealerRaw === "string" && assignedDealerRaw.trim() !== "" ? (assignedDealerRaw.trim() as Uuid) : null;
                return assignedDealerId !== null ? getOuDisplayName(assignedDealerId) : null;
            },
        },
        (event, bindings) => resolveListCardFooterWhenEnabled(listModuleConfig.showFooter, bindings, event.id),
        {
            onPhoneClick: (event, _bindings) => {
                const raw = event.eventContainer.additionalColumnValues[EMOTORAD__EVENT_CONTAINER__CONTACT_NUMBER__COLUMN_ID];
                ottopilotDialPhoneNumber(stringifyJourneyAdditionalValue(raw).trim());
            },
            onScheduleClick: (event, _footerId, _bindings) => {
                if (deps.rowEngagement !== undefined) {
                    deps.rowEngagement.openSchedule(event);
                    return;
                }
                navigateToEmotoradScheduleCallRoute(navigate, listModuleConfig.scheduleRoute, workspaceId, event.eventContainer.id);
            },
            onDisposeClick: (event, _footerId, _bindings) => {
                if (deps.rowEngagement !== undefined) {
                    deps.rowEngagement.openDisposition(event);
                    return;
                }
                onDisposeRequest(event);
            },
            onCreateClick: (event, _footerId, bindings) => {
                if (deps.rowEngagement !== undefined) {
                    deps.rowEngagement.openCreate(event);
                    return;
                }
                bindings.setSelectedCreateJourneyId(event.eventContainer.id);
                onOpenCreateDrawer();
            },
            onNotesClick: (event) => {
                if (deps.rowEngagement !== undefined) {
                    deps.rowEngagement.openNotes(event);
                    return;
                }
                openNotesPanel({
                    eventContainerId: event.eventContainer.id,
                    eventContainer: event.eventContainer,
                    createScope: {
                        scopeType: "event",
                        eventId: event.id,
                        eventTemplateId: event.templateId,
                    },
                    eventContainerSourceLabel: notesEventContainerSourceLabel,
                });
            },
            isListCardFooterRestricted: (event) => deps.isListCardFooterRestricted?.(event) === true,
        },
        createEmotoradJourneyListFunnelOptions((event) => event.eventContainer.additionalColumnValues),
        deps.sharedFooterBindings,
    );
}

export type EmotoradJourneysListCardPresentationDeps = {
    mode: "inQueue" | "lifecycle";
    workspaceId: Uuid;
    navigate: EmotoradScheduleNavigateFn;
    journeysDisplayNameOttopilotStringTemplate: string;
    emotoradInQueueListConfig: EmotoradListModuleConfig;
    emotoradLifecycleListConfig: EmotoradListModuleConfig;
    inQueueListStatusBoard: ListStatusBoardConfig;
    lifecycleListStatusBoard: ListStatusBoardConfig;
    lifecycleStatusColumnId: Uuid;
    getJourneyOuDisplayName: (journey: EventContainerWithEvents) => string | null;
    resolveInQueueAssignmentLabel: (journey: EventContainerWithEvents) => "Assigned" | "Unassigned";
    onLifecycleDisposeRequest: (journey: EventContainerWithEvents) => void;
    openNotesPanel: (context: ListNotesPanelOpenContext) => void;
    notesEventContainerSourceLabel?: string;
    openScheduleCallModal?: EmotoradListScheduleCallModalOpener;
    rowEngagement?: RowEngagementHandlers<EventContainerWithEvents>;
    sharedFooterBindings?: OttopilotListCardFooterBindings;
    isListCardFooterRestricted?: (journey: EventContainerWithEvents) => boolean;
};

export function useEmotoradJourneysListCardPresentation(deps: EmotoradJourneysListCardPresentationDeps) {
    const eventContainerTemplate = useOnlyEventContainerTemplate();
    const {navigate} = deps;
    const modeRef = useRef(deps.mode);
    modeRef.current = deps.mode;

    const depsRef = useRef(deps);
    depsRef.current = deps;

    const resolveLifecycleListStatus = createListStatusResolver<EventContainerWithEvents>({
        columnConfig: deps.lifecycleListStatusBoard.columnConfig,
        getStatusValue: (journey) => journey.additionalColumnValues[deps.lifecycleStatusColumnId],
        fallbackDisplayName: (journey) => eventContainerStatusToDisplayName(journey.status),
        resolveDisplayName: ({statusValue, matchedColumn}) => {
            if (matchedColumn !== undefined) {
                return matchedColumn.displayName;
            }
            const raw = statusValue === null || statusValue === undefined ? "" : String(statusValue).trim();
            if (raw === "") {
                return "";
            }
            return formatEmotoradJourneyLifecycleStatusIdForDisplay(raw);
        },
    });

    return useOttopilotListCardPresentation<EventContainerWithEvents>(
        {
            getDisplayName: (journey) =>
                processOttopilotStringTemplate(
                    depsRef.current.journeysDisplayNameOttopilotStringTemplate,
                    omitJourneyContactFromMergedValues(journey.additionalColumnValues),
                ),
            getPhoneNumber: (journey) => {
                const contactRaw = journey.additionalColumnValues[EMOTORAD__EVENT_CONTAINER__CONTACT_NUMBER__COLUMN_ID];
                return typeof contactRaw === "string" && contactRaw.trim() !== "" ? contactRaw : null;
            },
            getStatus: (journey) => {
                const d = depsRef.current;
                if (modeRef.current === "inQueue") {
                    const label = d.resolveInQueueAssignmentLabel(journey);
                    return resolveListCardStatusFromColumnConfig({
                        statusValue: label === "Assigned" ? LIST_INQUEUE_ASSIGNMENT_STATUS.assigned : LIST_INQUEUE_ASSIGNMENT_STATUS.unassigned,
                        columnConfig: d.inQueueListStatusBoard.columnConfig,
                    });
                }
                return resolveLifecycleListStatus(journey);
            },
            getCreatedAt: (journey) => journey.createdAt,
            getAssignedOuName: (journey) => depsRef.current.getJourneyOuDisplayName(journey),
        },
        (journey, bindings) => {
            const d = depsRef.current;
            if (modeRef.current !== "lifecycle") {
                return null;
            }
            return resolveListCardFooterWhenEnabled(d.emotoradLifecycleListConfig.showFooter, bindings, journey.id);
        },
        {
            onPhoneClick: (journey, _bindings) => {
                const contactRaw = journey.additionalColumnValues[EMOTORAD__EVENT_CONTAINER__CONTACT_NUMBER__COLUMN_ID];
                const contact = typeof contactRaw === "string" && contactRaw.trim() !== "" ? contactRaw : null;
                ottopilotDialPhoneNumber(contact);
            },
            onScheduleClick: (journey, _footerId, _bindings) => {
                const engagement = depsRef.current.rowEngagement;
                if (engagement !== undefined) {
                    engagement.openSchedule(journey);
                    return;
                }
                const d = depsRef.current;
                const route = modeRef.current === "inQueue" ? d.emotoradInQueueListConfig.scheduleRoute : d.emotoradLifecycleListConfig.scheduleRoute;
                navigateToEmotoradScheduleCallRoute(navigate, route, d.workspaceId, journey.id);
            },
            onDisposeClick: (journey, _footerId, _bindings) => {
                if (modeRef.current !== "lifecycle") {
                    return;
                }
                const engagement = depsRef.current.rowEngagement;
                if (engagement !== undefined) {
                    engagement.openDisposition(journey);
                    return;
                }
                depsRef.current.onLifecycleDisposeRequest(journey);
            },
            onCreateClick: (journey, _footerId, bindings) => {
                if (modeRef.current !== "lifecycle") {
                    return;
                }
                const engagement = depsRef.current.rowEngagement;
                if (engagement !== undefined) {
                    engagement.openCreate(journey);
                    return;
                }
                bindings.setSelectedCreateJourneyId(journey.id);
                bindings.setSelectedDispositoinId(
                    getOptionalEventContainerDispositionId(journey, eventContainerTemplate),
                );
            },
            onNotesClick: (journey) => {
                if (modeRef.current !== "lifecycle") {
                    return;
                }
                const engagement = depsRef.current.rowEngagement;
                if (engagement !== undefined) {
                    engagement.openNotes(journey);
                    return;
                }
                depsRef.current.openNotesPanel({
                    eventContainerId: journey.id,
                    eventContainer: journey,
                    createScope: {scopeType: "eventContainer"},
                    eventContainerSourceLabel: depsRef.current.notesEventContainerSourceLabel ?? "OttopilotEventContainer",
                });
            },
            isListCardFooterRestricted: (journey) =>
                depsRef.current.isListCardFooterRestricted?.(journey) === true,
        },
        createEmotoradJourneyListFunnelOptions((journey) => journey.additionalColumnValues),
        deps.sharedFooterBindings,
    );
}

export type EmotoradCustomerListCardRow = {
    id: Uuid;
    createdAt: Iso8601DateTime;
    eventContainer: OttopilotEventContainer | null;
    additionalColumnValues: Record<Uuid, unknown>;
};

export type EmotoradCustomersListCardPresentationDeps<T extends EmotoradCustomerListCardRow> = {
    workspaceId: Uuid;
    navigate: EmotoradScheduleNavigateFn;
    scheduleRoute: string;
    listModuleConfig: EmotoradListModuleConfig;
    customersModuleConfiguration: CustomersModuleConfiguration;
    customerStatusColumnId: Uuid;
    listStatusBoard: ListStatusBoardConfig;
    customerPhoneColumnId: Uuid;
    openNotesPanel: (context: ListNotesPanelOpenContext) => void;
    onOpenCreateDrawer: () => void;
    onDisposeRequest: (customer: T) => void;
    onOpenProfile?: (customer: T) => void;
};

export function useEmotoradCustomersListCardPresentation<T extends EmotoradCustomerListCardRow>(deps: EmotoradCustomersListCardPresentationDeps<T>) {
    const {
        workspaceId,
        navigate,
        scheduleRoute,
        listModuleConfig,
        customersModuleConfiguration,
        customerStatusColumnId,
        listStatusBoard,
        customerPhoneColumnId,
        openNotesPanel,
        onOpenCreateDrawer,
        onDisposeRequest,
        onOpenProfile,
    } = deps;

    const resolveCustomerListStatus = createListStatusResolver<T>({
        columnConfig: listStatusBoard.columnConfig,
        getStatusValue: (customer) => customer.additionalColumnValues[customerStatusColumnId],
        fallbackDisplayName: "Active",
        resolveDisplayName: ({statusValue, matchedColumn}) => {
            if (matchedColumn !== undefined) {
                return matchedColumn.displayName;
            }
            const mapped = Object.values(emotoradCustomerStatuses).find((s) => String(s.id) === statusValue);
            return mapped?.displayName ?? (statusValue !== "" ? statusValue : "Active");
        },
    });

    return useOttopilotListCardPresentation<T>(
        {
            getDisplayName: (customer) => {
                const merged: Record<Uuid, unknown> = omitJourneyContactFromMergedValues({
                    ...(customer.eventContainer !== null ? customer.eventContainer.additionalColumnValues : {}),
                    ...customer.additionalColumnValues,
                });
                const displayName = processOttopilotStringTemplate(customersModuleConfiguration.displayNameOttopilotStringTemplate, merged);
                return displayName === "" ? "Customer" : displayName;
            },
            getPhoneNumber: (customer) => {
                const phoneRaw = customer.additionalColumnValues[customerPhoneColumnId];
                const phone = phoneRaw === undefined || phoneRaw === null ? "" : stringifyJourneyAdditionalValue(phoneRaw).trim();
                return phone === "" ? null : phone;
            },
            getStatus: (customer) => resolveCustomerListStatus(customer),
            getCreatedAt: (customer) => customer.createdAt,
            getAssignedOuName: () => null,
        },
        (customer, bindings) => {
            if (listModuleConfig.showFooter !== true) {
                return null;
            }
            return bindings.getFooterControls(customer.id);
        },
        {
            onPhoneClick: (customer, _bindings) => {
                const phoneRaw = customer.additionalColumnValues[customerPhoneColumnId];
                const phone = phoneRaw === undefined || phoneRaw === null ? "" : stringifyJourneyAdditionalValue(phoneRaw).trim();
                ottopilotDialPhoneNumber(phone);
            },
            onScheduleClick: (customer, _footerId, _bindings) => {
                if (customer.eventContainer === null) {
                    navigate({
                        to: scheduleRoute,
                        params: {workspaceId},
                        search: {},
                    });
                    return;
                }
                navigateToEmotoradScheduleCallRoute(navigate, scheduleRoute, workspaceId, customer.eventContainer.id);
            },
            onDisposeClick: (customer, _footerId, _bindings) => {
                onDisposeRequest(customer);
            },
            onCreateClick: (customer, _footerId, bindings) => {
                bindings.setSelectedCreateJourneyId(customer.eventContainer?.id ?? null);
                onOpenCreateDrawer();
            },
            onNotesClick: (customer) => {
                if (customer.eventContainer === null) {
                    return;
                }
                openNotesPanel({
                    eventContainerId: customer.eventContainer.id,
                    eventContainer: customer.eventContainer,
                    createScope: {scopeType: "eventContainer"},
                    readOnly: true,
                    eventContainerSourceLabel: "Customers",
                });
            },
            onProfileClick:
                onOpenProfile === undefined
                    ? undefined
                    : (customer) => {
                          onOpenProfile(customer);
                      },
        },
        createEmotoradJourneyListFunnelOptions((customer) => (customer.eventContainer !== null ? customer.eventContainer.additionalColumnValues : {})),
    );
}

export type EmotoradDispositionTrackerListCardPresentationDeps = {
    workspaceId: Uuid;
    navigate: EmotoradScheduleNavigateFn;
    scheduleRoute: string;
    listModuleConfig: EmotoradListModuleConfig;
    mapListFields: (event: EventWithEventContainer) => {
        displayName: string;
        phone: string | null;
        status: ListCardStatus;
        assignedOuName: string | null;
        createdAt: Iso8601DateTime;
    };
    onDisposeRequest: (event: EventWithEventContainer) => void;
    openNotesPanel: (context: ListNotesPanelOpenContext) => void;
    notesEventContainerSourceLabel?: string;
    openScheduleCallModal?: EmotoradListScheduleCallModalOpener;
    rowEngagement?: RowEngagementHandlers<EventWithEventContainer>;
};

export function useEmotoradDispositionTrackerListCardPresentation(deps: EmotoradDispositionTrackerListCardPresentationDeps) {
    const eventTemplates = useEventTemplates();
    const notesEventContainerSourceLabel = deps.notesEventContainerSourceLabel ?? "OttopilotEventContainer";
    const {workspaceId, navigate, scheduleRoute, listModuleConfig, mapListFields, onDisposeRequest, openNotesPanel} = deps;

    const mapRef = useRef(mapListFields);
    mapRef.current = mapListFields;

    return useOttopilotListCardPresentation<EventWithEventContainer>(
        {
            getDisplayName: (event) => mapRef.current(event).displayName,
            getPhoneNumber: (event) => mapRef.current(event).phone,
            getStatus: (event) => {
                const mapped = mapRef.current(event);
                const status = mapped.status;
                return {
                    name: status.name === "" ? "Active" : status.name,
                    lozengeStyle: status.lozengeStyle,
                };
            },
            getCreatedAt: (event) => mapRef.current(event).createdAt,
            getAssignedOuName: (event) => mapRef.current(event).assignedOuName,
        },
        (event, bindings) => resolveListCardFooterWhenEnabled(listModuleConfig.showFooter, bindings, event.id),
        {
            onPhoneClick: (event, _bindings) => {
                ottopilotDialPhoneNumber(mapRef.current(event).phone);
            },
            onScheduleClick: (event, _footerId, _bindings) => {
                if (deps.rowEngagement !== undefined) {
                    deps.rowEngagement.openSchedule(event);
                    return;
                }
                navigateToEmotoradScheduleCallRoute(navigate, scheduleRoute, workspaceId, event.eventContainer.id);
            },
            onDisposeClick: (event, _footerId, _bindings) => {
                if (deps.rowEngagement !== undefined) {
                    deps.rowEngagement.openDisposition(event);
                    return;
                }
                onDisposeRequest(event);
            },
            onCreateClick: (event, _footerId, bindings) => {
                if (deps.rowEngagement !== undefined) {
                    deps.rowEngagement.openCreate(event);
                    return;
                }
                bindings.setSelectedCreateJourneyId(event.eventContainer.id);
                bindings.setSelectedDispositoinId(
                    getOptionalEventDispositionId(
                        event,
                        requireEventTemplate(eventTemplates, event.templateId),
                    ),
                );
            },
            onNotesClick: (event) => {
                if (deps.rowEngagement !== undefined) {
                    deps.rowEngagement.openNotes(event);
                    return;
                }
                openNotesPanel({
                    eventContainerId: event.eventContainer.id,
                    eventContainer: event.eventContainer,
                    createScope: {
                        scopeType: "event",
                        eventId: event.id,
                        eventTemplateId: event.templateId,
                    },
                    eventContainerSourceLabel: notesEventContainerSourceLabel,
                });
            },
        },
        createEmotoradJourneyListFunnelOptions((event) => event.eventContainer.additionalColumnValues),
    );
}
